import { useTranslation } from 'react-i18next';
import { Column, ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import ArrowDownIconSolid from '@/components/icons/ArrowDownIconSolid';
import NotificationsTypeDetails from '@/features/notifications/componentns/NotificationsTypeDetails';
import {
	AdminNotificationsListItemRecipientResponseDto,
	AdminNotificationsListItemResponseDto,
} from '@/features/notifications/types/notifications-dto';
import EDateText from '@/components/everridge/EDateText';
import EAvatar from '@/components/everridge/EAvatar';
import { useNotificationsFilter } from '@/features/notifications/hooks/useNotificationsFilter';

const columnClasses = 'text-sm text-popover-800';

const NotificationsPageColumns =
	(): ColumnDef<AdminNotificationsListItemResponseDto>[] => {
		const { t } = useTranslation();

		const { setFilter } = useNotificationsFilter();

		const handleSort = (
			columnKey: Capitalize<keyof AdminNotificationsListItemResponseDto>,
			column: Column<AdminNotificationsListItemResponseDto>,
		) => {
			const newDirection = column.getIsSorted() === 'asc';
			column.toggleSorting(newDirection);

			setFilter({
				orderBy: columnKey,
				orderDirection: newDirection ? 'asc' : 'desc',
			});
		};

		return [
			{
				accessorKey: 'sent',
				header: ({ column }) => {
					return (
						<Button
							variant='link'
							className={
								'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
							}
							onClick={() => handleSort('Sent', column)}>
							{t('main.notifications.table.header.sent')}
							<div
								className={`transition-transform duration-50 ease-in-out ${
									column.getIsSorted() === 'asc'
										? '-rotate-180'
										: 'rotate-0'
								}`}>
								{<ArrowDownIconSolid />}
							</div>
						</Button>
					);
				},
				cell: ({ row }) => (
					<div className={columnClasses}>
						<EDateText date={row.getValue('sent')} />
					</div>
				),
			},
			{
				accessorKey: 'title',
				header: ({ column }) => {
					return (
						<Button
							variant='link'
							className={
								'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
							}
							onClick={() => handleSort('Title', column)}>
							{t('main.notifications.table.header.message')}
							<div
								className={`transition-transform duration-50 ease-in-out ${
									column.getIsSorted() === 'asc'
										? '-rotate-180'
										: 'rotate-0'
								}`}>
								{<ArrowDownIconSolid />}
							</div>
						</Button>
					);
				},
				cell: ({ row }) => (
					<div className={`truncate ${columnClasses}`}>
						{row.getValue('title')}
					</div>
				),
				size: 200,
			},
			{
				accessorKey: 'message',
				header: ({ column }) => {
					return (
						<Button
							variant='link'
							className={
								'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
							}
							onClick={() => handleSort('Message', column)}>
							{t('main.notifications.table.header.message')}
							<div
								className={`transition-transform duration-50 ease-in-out ${
									column.getIsSorted() === 'asc'
										? '-rotate-180'
										: 'rotate-0'
								}`}>
								{<ArrowDownIconSolid />}
							</div>
						</Button>
					);
				},
				cell: ({ row }) => (
					<div className={`truncate ${columnClasses}`}>
						{row.getValue('message')}
					</div>
				),
				size: 200,
			},
			{
				accessorKey: 'recipients',
				header: ({ column }) => {
					return (
						<Button
							variant='link'
							className={
								'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
							}
							onClick={() => handleSort('Recipients', column)}>
							{t('main.notifications.table.header.recipients')}
							<div
								className={`transition-transform duration-50 ease-in-out ${
									column.getIsSorted() === 'asc'
										? '-rotate-180'
										: 'rotate-0'
								}`}>
								{<ArrowDownIconSolid />}
							</div>
						</Button>
					);
				},
				cell: ({ row }) => {
					const recipients: AdminNotificationsListItemRecipientResponseDto[] =
						row.getValue('recipients');

					const avatars = (quanity: number) =>
						recipients
							.map((rec) => rec.recipientAvatar)
							.slice(0, quanity);

					return (
						<div className={`flex ${columnClasses}`}>
							{recipients.length > 3 ? (
								<>
									{avatars(3).map((avatar, index) => (
										<EAvatar
											key={index}
											url={avatar}
											className={`size-8 ${
												index === 0 ? '' : '-ml-2.5'
											} border-2 border-popover-10`}
										/>
									))}
									<div
										className={
											'max-h-8 px-2.5 py-1.5 -ml-2.5 z-10 flex items-center border-2 border-popover-10 rounded-full bg-popover-100 text-sm text-popover-600'
										}>
										{`+${recipients.length - 3}`}
									</div>
								</>
							) : (
								avatars(3).map((avatar, index) => (
									<EAvatar
										key={index}
										url={avatar}
										className={`size-8 ${
											index === 0 ? '' : '-ml-2.5'
										} border-2 border-popover-10`}
									/>
								))
							)}
						</div>
					);
				},
			},
			{
				accessorKey: 'read',
				header: ({ column }) => {
					return (
						<Button
							variant='link'
							className={
								'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
							}
							onClick={() => handleSort('TotalRead', column)}>
							{t('main.notifications.table.header.read')}
							<div
								className={`transition-transform duration-50 ease-in-out ${
									column.getIsSorted() === 'asc'
										? '-rotate-180'
										: 'rotate-0'
								}`}>
								{<ArrowDownIconSolid />}
							</div>
						</Button>
					);
				},
				cell: ({ row }) => {
					const { totalRead, totalRecipients } = row.original;

					const message =
						totalRead === totalRecipients
							? `Everyone`
							: `${totalRead} of ${totalRecipients}`;

					return <div className={columnClasses}>{message}</div>;
				},
			},
			{
				accessorKey: 'isRequired',
				header: ({ column }) => {
					return (
						<Button
							variant='link'
							className={
								'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
							}
							onClick={() => handleSort('IsRequired', column)}>
							{t('main.notifications.table.header.type')}
							<div
								className={`transition-transform duration-50 ease-in-out ${
									column.getIsSorted() === 'asc'
										? '-rotate-180'
										: 'rotate-0'
								}`}>
								{<ArrowDownIconSolid />}
							</div>
						</Button>
					);
				},
				cell: ({ row }) => (
					<div className={columnClasses}>
						{
							<NotificationsTypeDetails
								type={row.getValue('isRequired')}
							/>
						}
					</div>
				),
			},
		];
	};

export default NotificationsPageColumns;
